/**
 * Pure utility functions for the scheduling interview picker.
 * Extracted for testability and reuse.
 */

export function generateTimeSlots(): { value: string; label: string }[] {
  const slots: { value: string; label: string }[] = []
  for (let h = 6; h < 24; h++) {
    for (const m of [0, 15, 30, 45]) {
      const hh = String(h).padStart(2, '0')
      const mm = String(m).padStart(2, '0')
      const value = `${hh}:${mm}`
      const hour12 = h % 12 || 12
      const ampm = h < 12 ? 'AM' : 'PM'
      const label = `${hour12}:${mm} ${ampm}`
      slots.push({ value, label })
    }
  }
  return slots
}

export const TIME_SLOTS = generateTimeSlots()

export function addMinutesToTime(time: string, minutes: number): string {
  const [h, m] = time.split(':').map(Number)
  const total = Math.min(h * 60 + m + minutes, 23 * 60 + 45)
  const newH = Math.floor(total / 60)
  const newM = total % 60
  return `${String(newH).padStart(2, '0')}:${String(newM).padStart(2, '0')}`
}

export function formatTimeLabel(time: string): string {
  const [h, m] = time.split(':').map(Number)
  const hour12 = h % 12 || 12
  const ampm = h < 12 ? 'AM' : 'PM'
  return `${hour12}:${String(m).padStart(2, '0')} ${ampm}`
}

/**
 * How far a zone sits from UTC at a given instant, in milliseconds.
 *
 * Derived by formatting the instant in the zone and reading the wall-clock
 * fields back — there is no direct API for it, and no zone database in the
 * bundle beyond what Intl already carries.
 */
function zoneOffsetMs(tz: string, at: Date): number {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: tz,
    hour12: false,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  }).formatToParts(at)

  const f: Record<string, number> = {}
  for (const p of parts) if (p.type !== 'literal') f[p.type] = Number(p.value)
  // `hour` comes back as 24 for midnight under hour12: false.
  const asUtc = Date.UTC(f.year, f.month - 1, f.day, f.hour % 24, f.minute, f.second)
  return asUtc - at.getTime()
}

/**
 * The instant at which a wall-clock time occurs **in a given zone**.
 *
 * UI-15 review: a booking's offset has to be read at the booked *time*, not at
 * the day's local midnight. On a transition day those differ — an afternoon New
 * York booking after the spring change is on GMT-4 while that morning is still
 * GMT-5 — so labelling from midnight is wrong for exactly the dates a reader is
 * most likely to be confused by.
 *
 * The wall fields are first treated as if they were UTC, then corrected by the
 * zone's offset. Two passes, because the offset used for the correction is
 * itself read at the corrected instant; a second pass settles the case where
 * the first guess landed on the other side of a transition.
 */
export function instantForZonedWallTime(tz: string, day: Date, time: string): Date {
  const [h = 0, m = 0] = time.split(':').map(Number)
  const wallAsUtc = Date.UTC(day.getFullYear(), day.getMonth(), day.getDate(), h, m)
  try {
    let guess = wallAsUtc - zoneOffsetMs(tz, new Date(wallAsUtc))
    guess = wallAsUtc - zoneOffsetMs(tz, new Date(guess))
    return new Date(guess)
  } catch {
    // Unresolvable zone — the wall time is the best available answer.
    return new Date(wallAsUtc)
  }
}

/**
 * The zone's offset at `on`, or `null` if the zone cannot be resolved.
 *
 * Separated from the formatting below so a *valid* zero offset (Europe/London
 * in winter) stays distinguishable from a formatter failure. Comparing the
 * rendered string against 'GMT' cannot tell those apart, which is what made
 * London lose its offset in the picker (UI-15 review).
 */
function zoneOffsetLabel(tz: string, on: Date): string | null {
  try {
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone: tz,
      timeZoneName: 'shortOffset',
    }).formatToParts(on)
    return parts.find((p) => p.type === 'timeZoneName')?.value ?? null
  } catch {
    return null
  }
}

/**
 * The GMT offset a zone is on **for a given instant**.
 *
 * UI-15 (Hasma): "The offset doesn't update with the selected date — picking
 * Thu, Nov 19 still renders GMT-7, but Los Angeles is on PST (GMT-8) in
 * November, so the label is wrong for any date outside daylight saving."
 *
 * The offset is a property of (zone, instant), not of the zone alone: it moves
 * at each DST transition. Formatting `new Date()` therefore labelled every
 * booking with *today's* offset. `on` defaults to now so a caller with no
 * particular instant still gets the current offset.
 */
export function getTimezoneGMTOffset(tz: string, on: Date = new Date()): string {
  return zoneOffsetLabel(tz, on) ?? 'GMT'
}

/**
 * A zone labelled the way both the picker and the summary show it.
 *
 * UI-15 (Hasma): "the picker shows the IANA identifier (America/Los Angeles)
 * while the summary line shows a UTC offset (GMT-7) so users have to know those
 * refer to the same zone." Naming both in one label removes that step.
 *
 * `on` matters for the same reason it does in getTimezoneGMTOffset — the offset
 * shown must be the one in force at the instant being labelled.
 */
export function formatTimezoneLabel(tz: string, on: Date = new Date()): string {
  const name = tz.replace(/_/g, ' ')
  const offset = zoneOffsetLabel(tz, on)
  return offset ? `${name} (${offset})` : name
}

export const getUserTimezone = () =>
  Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC'

/**
 * Convert a local date string + time string + IANA timezone to a UTC ISO string.
 * e.g. date="2025-09-01", time="14:00", tz="America/New_York" → "2025-09-01T18:00:00.000Z"
 *
 * The offset is read at the booked instant, as `instantForZonedWallTime` does
 * (Greptile on #37104): reading it at the wall time taken as UTC put a 06:00
 * Los Angeles booking on Nov 1 2026, the morning the clocks go back, at 13:00Z
 * instead of 14:00Z. Unlike that helper, an unknown zone still throws here:
 * a booking is sent on this, so it must not fall back to a guess.
 */
export function toUTCISO(date: string, time: string, tz: string): string {
  const [year, month, day] = date.split('-').map(Number)
  const [hours, minutes] = time.split(':').map(Number)
  const wallAsUtc = Date.UTC(year, month - 1, day, hours, minutes, 0)
  let utcMs = wallAsUtc - zoneOffsetMs(tz, new Date(wallAsUtc))
  utcMs = wallAsUtc - zoneOffsetMs(tz, new Date(utcMs))
  return new Date(utcMs).toISOString()
}

/**
 * The UTC start and end an interview is created with. A booked slot converts
 * its wall times in its zone; "Immediately" starts at `now` and runs for its
 * duration, so the bots join as soon as the interview is created rather than at
 * the next quarter hour.
 */
export function interviewWindowUTC(
  schedule: {
    date: string
    startTime: string
    endTime: string
    duration: number
    timezone: string
    immediate?: boolean
  },
  now: Date = new Date(),
): { start: string; end: string } {
  if (schedule.immediate) {
    return {
      start: now.toISOString(),
      end: new Date(now.getTime() + schedule.duration * 60_000).toISOString(),
    }
  }
  return {
    start: toUTCISO(schedule.date, schedule.startTime, schedule.timezone),
    end: toUTCISO(schedule.date, schedule.endTime, schedule.timezone),
  }
}
